/**
 * useIrisSearch.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Shared "Search Iris" flow: checks the local Fident PSE service, opens the
 * iris scanner window, looks up the scanned EnrolleeID against the local/
 * national iris repository, and resolves to a single matched person record.
 *
 * This is the same flow used on the main Folder Search page
 * (src/pages/FolderSearch/index.jsx), extracted so it can be reused from
 * other folder-search entry points (e.g. the "Search for Folder" modal used
 * from Intake Queue) without duplicating the scanning/dialog logic.
 *
 * Consumers decide what "resolved" means for them by supplying `onResolved`
 * — e.g. the main Folder Search page opens the matched booking directly,
 * while a search-and-pick modal just pre-fills the name fields and re-runs
 * the search.
 *
 * USAGE:
 *   const {
 *     irisLoading,
 *     irisSearchData,
 *     irisSearchDetailsPopupOpen,
 *     setisSearchIrisDetailsPopupOpen,
 *     openIrisSearch,
 *   } = useIrisSearch({
 *     onResolved: (irisRecord, isLocalDataMatched) => { ... },
 *   });
 */

import { useState } from "react";
import { useDispatch } from "react-redux";
import api from "@/services/axios_custom";
import API_ENDPOINTS from "@/util/api";
import notificationService from "@/util/notificationService";
import useDialog from "@/hooks/useDialog";
import { setIrisEnrolleId, setIrisSearchId } from "@/slices/bookingsSlice";
import { isNonEmptyVariable } from "@/util/common_function";

const FIDENT_PSE_STATUS_URL = "http://localhost:6191/iris/check-fidentPSE";
const FIDENT_PSE_SEARCH_URL = "http://localhost:6191/iris/searchIris";

export function useIrisSearch({
  onResolved,
  skipMatchConfirmation = false,
  useIrishEndpoint = false,
}) {
  const dispatch = useDispatch();
  const { showDialog } = useDialog();
  const [irisLoading, setIrisLoading] = useState(false);
  const [irisSearchData, setIrisSearchData] = useState();
  const [irisSearchDetailsPopupOpen, setisSearchIrisDetailsPopupOpen] =
    useState(false);

  const irisSearchDataEmit = (irisData) => {
    setIrisSearchData(irisData);
    if (isNonEmptyVariable(irisData)) {
      setTimeout(() => {
        setisSearchIrisDetailsPopupOpen(true);
      }, 500);
    }
  };

  const getMessage = (enrollee_id, isLocalMatch) => (
    <>
      <p>
        <b>*NOTE</b>: The inmate just scanned has {!isLocalMatch && "no"} IRIS
        enrolled folder in the local system
        {!isLocalMatch && ", but WAS found in the national IRIS repository"}.
      </p>
      <p>
        Would you like to see additional info about IRIS EnrolleeID{" "}
        {enrollee_id} from the national IRIS repository?
      </p>
    </>
  );

  const noDataFound = (irisData) => {
    showDialog("info", {
      title: "Iris Data Not Found",
      content: (
        <div>
          <p>
            Iris data is not found for these eyes, please enter search criteria
            and perform a folder search
          </p>
          {(irisData?.FirstName || irisData?.LastName) && (
            <p>
              <b>First Name:</b> {irisData?.FirstName || ""}
              &nbsp;&nbsp;
              <b>Last Name:</b> {irisData?.LastName || ""}
            </p>
          )}
        </div>
      ),
      confirmButtonText: "Ok",
      onConfirm: () => {
        irisSearchDataEmit(null);
        setIrisSearchData(null);
        dispatch(setIrisEnrolleId(0));
      },
    });
  };

  const resolveMatch = (
    irisRecord,
    isLocalDataMatched,
    skipConfirmation = false,
  ) => {
    if (isLocalDataMatched || skipConfirmation) {
      onResolved?.(irisRecord, isLocalDataMatched);
      return;
    }

    showDialog("confirm", {
      title: "Folder Not Found",
      content: (
        <div>
          <p>
            No Folder is found for these eyes. This record has First Name and
            Last Name data, would you like to perform a folder search using
            these values?
          </p>
          <p>
            <b>First Name:</b> {irisRecord?.firstName || ""}
            &nbsp;&nbsp;
            <b>Last Name:</b> {irisRecord?.lastName || ""}
          </p>
        </div>
      ),
      confirmButtonText: "Yes",
      cancelButtonText: "No",
      onConfirm: () => onResolved?.(irisRecord, false),
    });
  };

  const openIrisScannerWindow = () => {
    setIrisLoading(true);
    api
      .get(FIDENT_PSE_SEARCH_URL)
      .then((res) => {
        if (res?.data?.Result === "Cancel") {
          showDialog("info", {
            title: "Cancelled by User",
            confirmButtonText: "Ok",
          });
          setIrisLoading(false);
          return;
        }

        if (!isNonEmptyVariable(res?.data)) {
          dispatch(setIrisSearchId(0));
          noDataFound();
          setIrisLoading(false);
          return;
        }

        const resDataPre = res.data;
        dispatch(setIrisSearchId(resDataPre?.TransactionId || 0));
        dispatch(setIrisEnrolleId(resDataPre?.EnrolleeId || 0));

        if (!resDataPre?.EnrolleeId) {
          if (useIrishEndpoint) {
            irisSearchDataEmit([]);
            setIrisLoading(false);
            return;
          }
          noDataFound(resDataPre);
          setIrisLoading(false);
          return;
        }

        // Document Control's Search Iris uses a dedicated endpoint that
        // returns an already-flat array of matched booking records (no
        // enrolledIrisList/isLocalDataMatched wrapper, no confirm dialog).
        if (useIrishEndpoint) {
          const urlIrish = API_ENDPOINTS.irish + resDataPre?.EnrolleeId;

          api
            .get(urlIrish)
            .then((resIrish) => {
              irisSearchDataEmit(resIrish?.data ?? []);
            })
            .finally(() => {
              setIrisLoading(false);
            });
          return;
        }

        const urlList =
          API_ENDPOINTS.irisListSearch +
          `?enrollId=${resDataPre?.EnrolleeId}&searchId=${resDataPre?.TransactionId}`;

        api
          .get(urlList)
          .then((resList) => {
            const enrolledIrisList = resList?.data?.enrolledIrisList;
            if (!enrolledIrisList?.length) {
              dispatch(setIrisSearchId(resList?.data?.searchId || 0));
              noDataFound(resDataPre);
              return;
            }

            const resData = enrolledIrisList[0];
            if (resData.enrolleeId === 0) {
              dispatch(setIrisSearchId(resList?.data?.searchId || 0));
              noDataFound(resDataPre);
              return;
            }

            const isLocalMatch = resList?.data?.isLocalDataMatched;

            if (skipMatchConfirmation) {
              irisSearchDataEmit(resList?.data);
              return;
            }

            showDialog("confirm", {
              title: isLocalMatch
                ? "Local Iris Match!"
                : "National Iris Match!",
              content: getMessage(resData?.enrolleeId, isLocalMatch),
              onConfirm: () => {
                let da = resList?.data;
                if (isLocalMatch) {
                  da = {
                    searchId: resList?.data?.searchId,
                    isLocalDataMatched: resList?.data?.isLocalDataMatched,
                    enrolledIrisList: resList?.data?.enrolledIrisList?.filter(
                      (e) => e?.isLocalDataMatched,
                    ),
                    photos: resList?.data?.photos,
                  };
                }
                irisSearchDataEmit(da);
              },
              onCancel: () => {
                resolveMatch(enrolledIrisList[0], isLocalMatch);
              },
              confirmButtonText: "Yes",
              cancelButtonText: "No",
            });
          })
          .finally(() => {
            setIrisLoading(false);
          });
      })
      .catch(() => {
        dispatch(setIrisSearchId(0));
        noDataFound();
        setIrisLoading(false);
      });
  };

  const openIrisSearch = () => {
    setIrisLoading(true);
    api
      .get(FIDENT_PSE_STATUS_URL)
      .then((response) => {
        if (response?.data?.status === "Success") {
          dispatch(setIrisSearchId(0));
          dispatch(setIrisEnrolleId(0));
          openIrisScannerWindow();
        } else {
          setIrisLoading(false);
          notificationService.error(
            "Fident PSE service is not running. Please start the service to use Iris Search.",
          );
        }
      })
      .catch((error) => {
        console.error("Error checking FidentPSE status:", error);
        setIrisLoading(false);
        notificationService.error(
          "Fident PSE service is not running. Please start the service to use Iris Search.",
        );
      });
  };

  // Called by the caller when the user picks "Continue" in the details popup
  const returnFromDetailsPopup = (selectedIrisData, isLocalDataMatched) => {
    resolveMatch(selectedIrisData, isLocalDataMatched, true);
  };

  return {
    irisLoading,
    irisSearchData,
    irisSearchDetailsPopupOpen,
    setisSearchIrisDetailsPopupOpen,
    openIrisSearch,
    returnFromDetailsPopup,
  };
}
